import { useMemo } from 'react'
import type { AnalysisTimeRangeKey } from '../../../../types/analysis'
import type { ChatMember } from '../../../../context/chatContextCore'
import type { TimelineElement } from '../../../../types/timelineElement'
import type { LiveModuleHeadline, LiveModuleRecord, LiveModuleSparkline } from '../LiveModuleCard'
import { bucketize, calculateDelta, timeRangeBucketCount, timeRangeToDateRange } from './bucketize'

export interface UseRoleLoadModuleParams {
  workspaceId: string
  members?: ChatMember[]
  elements?: TimelineElement[]
  timeRange?: AnalysisTimeRangeKey
  refreshKey?: number
  now?: Date
}

export interface UseRoleLoadModuleResult {
  headline: LiveModuleHeadline
  sparkline: LiveModuleSparkline
  records: LiveModuleRecord[]
  loading: boolean
  error: Error | null
}

const parseDate = (val: unknown): Date | null => {
  if (!val) return null
  if (val instanceof Date) return val
  if (typeof (val as { toDate?: () => Date }).toDate === 'function') {
    return (val as { toDate: () => Date }).toDate()
  }
  if (typeof val === 'string' || typeof val === 'number') {
    const d = new Date(val)
    return isNaN(d.getTime()) ? null : d
  }
  return null
}

const formatRoleLabel = (role: string): string => {
  const clean = role.toLowerCase()
  return clean.charAt(0).toUpperCase() + clean.slice(1)
}

export const useRoleLoadModule = ({
  workspaceId,
  members = [],
  elements = [],
  timeRange = '7D',
  refreshKey,
  now = new Date(),
}: UseRoleLoadModuleParams): UseRoleLoadModuleResult => {
  return useMemo(() => {
    // refreshKey dependency triggers re-calculation when user requests new snapshot
    void refreshKey

    const bucketCount = timeRangeBucketCount(timeRange)

    if (!workspaceId || members.length === 0) {
      return {
        headline: { value: '—', label: 'no records' },
        sparkline: { points: Array(bucketCount).fill(0), direction: 'up-is-good' },
        records: [],
        loading: false,
        error: null,
      }
    }

    const memberByUid = new Map<string, ChatMember>()
    const roleMemberCounts = new Map<string, number>()

    for (const member of members) {
      const uid = member.uid || member.id
      if (uid) memberByUid.set(uid, member)
      const roleKey = (member.role || 'viewer').toUpperCase()
      roleMemberCounts.set(roleKey, (roleMemberCounts.get(roleKey) ?? 0) + 1)
    }

    // Correlate active checklist items from elements to roles
    const roleActiveItemCounts = new Map<string, number>()
    const workspaceElements = elements.filter((el) => el.workspaceId === workspaceId)

    for (const el of workspaceElements) {
      for (const item of el.checklist ?? []) {
        const isCompleted = item.isCompleted || item.workflowStatus === 'COMPLETED' || item.workflowStatus === 'NOT_REQUIRED'
        if (!isCompleted && item.assigneeId) {
          const assignee = memberByUid.get(item.assigneeId)
          const roleKey = (assignee?.role || 'viewer').toUpperCase()
          roleActiveItemCounts.set(roleKey, (roleActiveItemCounts.get(roleKey) ?? 0) + 1)
        }
      }
    }

    // Sparkline derivation: use joinedAt/createdAt timestamps if available per Rule 5
    const { from, to } = timeRangeToDateRange(timeRange, now)
    const validDates = members
      .map((m) => parseDate((m as { joinedAt?: unknown; createdAt?: unknown }).joinedAt ?? (m as { joinedAt?: unknown; createdAt?: unknown }).createdAt))
      .filter((d): d is Date => d !== null && d >= from && d <= to)

    let points: number[]
    let delta: number
    if (validDates.length > 0) {
      points = bucketize(validDates, timeRange, now)
      delta = calculateDelta(points)
    } else {
      // If member items don't carry a usable timestamp field, fall back to points: []
      points = []
      delta = 0
    }

    // Top-5 roles ranked by member count descending
    const standardRoles = ['ADMIN', 'EDITOR', 'VIEWER', 'HELPER']
    const presentRoles = Array.from(roleMemberCounts.keys())
    const allRoleKeys = Array.from(new Set([...standardRoles, ...presentRoles]))

    const records: LiveModuleRecord[] = allRoleKeys
      .map((roleKey) => {
        const memberCount = roleMemberCounts.get(roleKey) ?? 0
        const activeItemCount = roleActiveItemCounts.get(roleKey) ?? 0
        return {
          roleKey,
          memberCount,
          activeItemCount,
        }
      })
      .filter((r) => r.memberCount > 0)
      .sort((a, b) => b.memberCount - a.memberCount || b.activeItemCount - a.activeItemCount)
      .slice(0, 5)
      .map((r, index) => ({
        id: `role-${r.roleKey.toLowerCase()}-${index}`,
        primary: formatRoleLabel(r.roleKey),
        secondary: `${r.activeItemCount} active item${r.activeItemCount === 1 ? '' : 's'} · ${r.memberCount} member${r.memberCount === 1 ? '' : 's'}`,
        numeric: r.memberCount,
        drillTarget: 'CHECKLISTS',
      }))

    const headline: LiveModuleHeadline = {
      value: members.length,
      delta,
      label: 'members',
    }

    return {
      headline,
      sparkline: { points, direction: 'up-is-good' },
      records,
      loading: false,
      error: null,
    }
  }, [workspaceId, members, elements, timeRange, refreshKey, now])
}
